import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { StorageService } from "../../../services/storage.service";
import { Uris } from "../../../services/Uris";
import {
  Brand,
  BrandBody,
  BrandGalleryItem,
  PaginatedBrands
} from "../interfaces/brand.interfaces";

@Injectable({
  providedIn: "root"
})
export class BrandsService {
  constructor(
    private http: HttpClient,
    public router: Router,
    public storageService: StorageService
  ) {}

  /**
   * Obtiene la lista paginada de marcas de alimentadoras.
   * @param query Cadena de query params, ej: `?page=1&page_size=30`.
   * @returns Observable con la respuesta paginada.
   */
  listBrands(query: string = ""): Observable<PaginatedBrands> {
    return this.http
      .get<PaginatedBrands>(`${Uris.FEEDER_BRAND}${query}`, this.storageService.headers())
      .pipe(
        map((resp: PaginatedBrands) => {
          return resp;
        })
      );
  }

  /**
   * Obtiene una marca de alimentadora por su ID.
   * @param id Identificador de la marca.
   * @returns Observable con los datos de la marca.
   */
  getBrandById(id: number): Observable<Brand> {
    return this.http.get<Brand>(`${Uris.FEEDER_BRAND}${id}/`, this.storageService.headers()).pipe(
      map((resp: Brand) => {
        return resp;
      })
    );
  }

  /**
   * Crea una nueva marca de alimentadora.
   * @param value Datos de la nueva marca.
   * @returns Observable con la marca creada.
   */
  createBrand(value: BrandBody): Observable<Brand> {
    return this.http.post<Brand>(`${Uris.FEEDER_BRAND}`, value, this.storageService.headers()).pipe(
      map((resp: Brand) => {
        this.storageService.animation(
          "success",
          "Éxito",
          `Marca de alimentadora ${resp.name} creada correctamente`
        );
        return resp;
      })
    );
  }

  /**
   * Edita una marca de alimentadora existente.
   * @param id Identificador de la marca.
   * @param value Datos a actualizar.
   * @returns Observable con la marca actualizada.
   */
  editBrand(id: number, value: BrandBody): Observable<Brand> {
    return this.http
      .patch<Brand>(`${Uris.FEEDER_BRAND}${id}/`, value, this.storageService.headers())
      .pipe(
        map((resp: Brand) => {
          this.storageService.animation(
            "success",
            "Éxito",
            `Marca de alimentadora ${resp.name} editada correctamente`
          );
          return resp;
        })
      );
  }

  /**
   * Agrega una imagen a la galería de una marca.
   * @param id Identificador de la marca.
   * @param value Objeto con el archivo de imagen en base64.
   * @returns Observable con la respuesta del servidor.
   */
  addBrandPicture(id: number | null, value: any): Observable<any> {
    return this.http
      .post(`${Uris.FEEDER_BRAND}${id}/add_picture/`, value, this.storageService.headers())
      .pipe(
        map((resp: any) => {
          this.storageService.animation("success", "Éxito", `Se agregó la imágen correctamente`);
          return resp;
        })
      );
  }

  /**
   * Obtiene la galería de imágenes de una marca.
   * @param id Identificador de la marca.
   * @returns Observable con la lista de imágenes.
   */
  listBrandGallery(id: number | null): Observable<BrandGalleryItem[]> {
    return this.http
      .get<BrandGalleryItem[]>(`${Uris.FEEDER_BRAND}${id}/gallery/`, this.storageService.headers())
      .pipe(
        map((resp: BrandGalleryItem[]) => {
          return resp;
        })
      );
  }

  /**
   * Elimina una imagen de la galería de una marca.
   * @param brandId Identificador de la marca.
   * @param imageId Identificador de la imagen.
   * @returns Observable con la respuesta del servidor.
   */
  deleteBrandPicture(brandId: number, imageId: any): Observable<any> {
    return this.http
      .delete(
        `${Uris.FEEDER_BRAND}${brandId}/remove_picture/${imageId}/`,
        this.storageService.headers()
      )
      .pipe(
        map((resp: any) => {
          this.storageService.animation("success", "Éxito", `Se eliminó la imágen correctamente`);
          return resp;
        })
      );
  }

  /**
   * Elimina una marca de alimentadora por su ID.
   * @param id Identificador de la marca.
   * @returns Observable con la respuesta del servidor.
   */
  deleteBrand(id: number): Observable<any> {
    return this.http.delete(`${Uris.FEEDER_BRAND}${id}/`, this.storageService.headers()).pipe(
      map((resp: any) => {
        this.storageService.animation(
          "success",
          "Éxito",
          `Marca de alimentadora eliminada correctamente`
        );
        return resp;
      })
    );
  }
}
